import { driver } from "driver.js";
import "driver.js/dist/driver.css";

const ONBOARDING_KEY = "admin_onboarding_completed";
const USER_ONBOARDING_KEY = "user_onboarding_completed";

export const hasCompletedOnboarding = () => {
    return localStorage.getItem(ONBOARDING_KEY) === "true";
};

export const hasCompletedUserOnboarding = () => {
    return localStorage.getItem(USER_ONBOARDING_KEY) === "true";
};

export const markOnboardingComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
};

export const markUserOnboardingComplete = () => {
    localStorage.setItem(USER_ONBOARDING_KEY, "true");
};

export const shouldShowOnboarding = () => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    return role === "admin" && !hasCompletedOnboarding();
};

export const shouldShowUserOnboarding = () => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    return role === "user" && !hasCompletedUserOnboarding();
};

export const startOnboardingTour = (navigate: (path: string) => void) => {
    const driverObj = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        overlayColor: "rgba(0, 0, 0, 0.6)",
        stagePadding: 8,
        stageRadius: 12,
        popoverClass: "onboarding-popover",
        onDestroyStarted: () => {
            markOnboardingComplete();
            driverObj.destroy();
        },
        steps: [
            {
                popover: {
                    title: "👋 Welcome to Codenzic ERP!",
                    description:
                        "Let's walk you through the initial setup steps to get your business running. Follow these steps in order for the best experience.",
                    side: "over" as any,
                    align: "center" as any,
                },
            },
            {
                element: '[data-tour="users"]',
                popover: {
                    title: "Step 1: Create User Accounts",
                    description:
                        "Start by creating user accounts for your staff. Each user will get login credentials to access the system.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/users");
                        driverObj.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="staff-management"]',
                popover: {
                    title: "Step 2: Assign Branch & Counter",
                    description:
                        "Go to Staff Management to assign each user to a branch and counter. This determines where they can operate.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/staff-management");
                        driverObj.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="category"]',
                popover: {
                    title: "Step 3: Create Categories",
                    description:
                        "Set up product categories to organize your inventory. You can create main categories and sub-categories.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/category");
                        driverObj.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="brands"]',
                popover: {
                    title: "Step 4: Create Brands",
                    description:
                        "Add the brands you sell. This helps filter and organize products in your catalog.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/brands");
                        driverObj.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="product"]',
                popover: {
                    title: "Step 5: Create Products",
                    description:
                        "Now add your products with pricing, units, tax rates, and assign them to categories and brands.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/product");
                        driverObj.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="vendor-management"]',
                popover: {
                    title: "Step 6: Create Vendors",
                    description:
                        "Finally, add your vendors/suppliers. You'll need vendors to create purchase entries and manage procurement.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/vendor-management");
                        driverObj.moveNext();
                    },
                },
            },
            {
                popover: {
                    title: "🎉 You're All Set!",
                    description:
                        "Your initial setup is complete. You can now start creating purchases, invoices, and managing your business. You can always access these sections from the sidebar.",
                    side: "over" as any,
                    align: "center" as any,
                },
            },
        ],
    });

    // Small delay to ensure DOM is ready
    setTimeout(() => {
        driverObj.drive();
    }, 500);

    return driverObj;
};


export const startUserOnboardingTour = (navigate: (path: string) => void) => {
    const driverObj = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        overlayColor: "rgba(0, 0, 0, 0.6)",
        stagePadding: 8,
        stageRadius: 12,
        popoverClass: "onboarding-popover",
        onDestroyStarted: () => {
            markUserOnboardingComplete();
            driverObj.destroy();
        },
        steps: [
            {
                popover: {
                    title: "👋 Welcome to Your Workspace!",
                    description:
                        "Here's a quick guide on your daily workflow. Follow these steps each day to keep everything running smoothly.",
                    side: "over" as any,
                    align: "center" as any,
                },
            },
            {
                element: '[data-tour="accounting-list"]',
                popover: {
                    title: "Step 1: Start Accounting Session",
                    description:
                        "Begin your day by opening an accounting session. This confirms your opening cash and bank balances before you can start transactions.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/accounting-list");
                        driverObj.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="banking"]',
                popover: {
                    title: "Step 2: Add Current Balances",
                    description:
                        "Use the Banking module to add your current balances. Use 'Cash In' for physical cash and 'Bank In' for bank deposits to record your starting amounts.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/banking");
                        driverObj.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="purchase"]',
                popover: {
                    title: "Step 3: Purchase Products",
                    description:
                        "Go to the Purchase module to create purchase entries. This records products you've bought from vendors and adds them to your inventory.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/purchase");
                        driverObj.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="inventory"]',
                popover: {
                    title: "Step 4: Configure Inventory",
                    description:
                        "Check and configure inventory settings for your purchased products — verify stock levels, units, and pricing are correct.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/inventory");
                        driverObj.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="sales"]',
                popover: {
                    title: "Step 5: Start Invoicing",
                    description:
                        "Now you're ready to sell! Use Draft for quick saves, Quotation for customer proposals, or Invoice for direct sales. All options are under the Sales menu.",
                    side: "right" as any,
                    align: "start" as any,
                    onNextClick: () => {
                        navigate("/sales");
                        driverObj.moveNext();
                    },
                },
            },
            {
                popover: {
                    title: "⚠️ Important: End Your Session",
                    description:
                        "Don't forget to close your accounting session before 12 AM! Go to the Accounting page and click 'Close & Submit' with your closing balances. This ensures your daily records are accurate.",
                    side: "over" as any,
                    align: "center" as any,
                },
            },
            {
                popover: {
                    title: "🎉 You're Ready!",
                    description:
                        "That's your daily workflow: Open Session → Add Balances → Purchase → Inventory → Invoice → Close Session. You've got this!",
                    side: "over" as any,
                    align: "center" as any,
                },
            },
        ],
    });

    setTimeout(() => {
        driverObj.drive();
    }, 500);

    return driverObj;
};
